import * as React from 'react'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import groupBy from 'lodash/groupBy'

import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useFetchData from 'modules/attendance/components/SettingItemPage/TimeTracking/TimeTrackingPolicy/useFetchData'
import { useFetchCustomTimeRangeCategories } from 'modules/attendance/hooks/useFetchCustomTimeRangeCategories'
import useLegalEntities from 'modules/attendance/hooks/useLegalEntities'
import { CustomTimeRangesFormData } from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'
import { useFetchContractsTaxonomies } from 'modules/contracts/hooks/useFetchContractsTaxonomies'
import { useFetchCustomTimeRangeCategorySupplementMappings } from 'modules/payroll_bundle/hooks/useFetchReimbursableSupplementMappings'

import TimeMappingDropdown from './TimeMappingDropdown'
import { Step } from './utils/stepTypes.js'

interface Props {
  form: FormikProps<CustomTimeRangesFormData>
  onClose: () => void
  onSubmit: () => void
  goBack: () => void
  settingId: string
  step: Step
  totalSteps: number
}

const PayrollSupplementModal = ({
  form,
  onClose,
  onSubmit,
  goBack,
  settingId,
  totalSteps,
  step,
}: Props) => {
  const { attendancePolicy, loading: loadingAttendancePolicy } = useFetchData(settingId)
  const employeesIds = attendancePolicy?.attributes.active_employees
  const { legalEntities, loading: loadingLegalEntities } = useLegalEntities({
    employeesIds,
  })
  const legalEntityIds = legalEntities.map((l) => l.id)
  const { categories: attendanceCategories, loading: loadingCustomTimeRangeCategories } =
    useFetchCustomTimeRangeCategories(settingId)
  const { taxonomies: supplementsTaxonomies, loading: loadingTaxonomies } =
    useFetchContractsTaxonomies(legalEntityIds)
  const { mappings, loading: loadingMappings } = useFetchCustomTimeRangeCategorySupplementMappings(
    legalEntityIds.map((id) => id)
  )

  const loading =
    loadingAttendancePolicy ||
    loadingLegalEntities ||
    loadingTaxonomies ||
    loadingMappings ||
    loadingCustomTimeRangeCategories

  const currentSettingAttendanceCategory = attendanceCategories.findCategoryById(
    String(form.values.attendance_custom_time_range_category_id)
  )
  const timeSettingsCategoryId =
    currentSettingAttendanceCategory?.timeSettingsCustomTimeRangeCategoryId

  const findMapping = (legalEntityId: string) =>
    mappings.find(
      (mapping) =>
        mapping.legalEntityId === legalEntityId &&
        mapping.reimbursableId === timeSettingsCategoryId?.toString()
    )

  const taxonomiesByLegalEntity = groupBy(
    supplementsTaxonomies.activeTaxonomies(),
    (taxonomy) => taxonomy.legalEntityId
  )

  const footer = (
    <>
      <Button type="secondary" onClick={() => goBack()}>
        {i18n.t('common.back')}
      </Button>
      <Button
        onClick={() => {
          track(trackingEvents.timeTracking.payrollMappingsModalSavePressed, {
            legal_entites_count: legalEntities.length,
          })
          onSubmit()
        }}
      >
        {i18n.t('common.save')}
      </Button>
    </>
  )

  if (!timeSettingsCategoryId)
    throw new Error(
      i18n.t('time_tracking.settings.time_blocks.categories.modals.time_settings_id_not_found')
    )

  return (
    <Modal
      illustration="night"
      title={i18n.t('time_tracking.settings.time_blocks.categories.modals.payroll_mappings_title')}
      description=""
      onClose={onClose}
      onSubmit={onSubmit}
      footer={footer}
      progress={{ amount: step, total: totalSteps }}
    >
      {loading ? (
        <Box alignItems="center" height="full">
          <Spinner />
        </Box>
      ) : (
        <Box background="grey200" padding="s16" gap="s16">
          {legalEntities.map((legalEntity) => (
            <Box key={legalEntity.id}>
              <Text color="grey1000" size="200" weight="medium">
                {legalEntity.legalName}
              </Text>
              <TimeMappingDropdown
                timeRangeCategoryId={timeSettingsCategoryId}
                legalEntityId={legalEntity.id}
                taxonomies={taxonomiesByLegalEntity[legalEntity.id] || []}
                mapping={findMapping(legalEntity.id.toString())}
                form={form}
              />
            </Box>
          ))}
        </Box>
      )}
    </Modal>
  )
}

export default PayrollSupplementModal
